import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import keyBy from 'lodash/keyBy';
import toArray from 'lodash/toArray';
import omit from 'lodash/omit';
import get from 'lodash/get';

import { api, apiV2 } from 'api';

const initialState = {
  devices: {},
  isLoading: false,
  error: null,
  subscriptionPurchaseResult: null,
  currentMaxDevices: null, // Will be set from API responses
  currentExtraDevices: null, // Will be set from API responses
};

// Device CRUD operations
export const actions = {
  editDevice: createAsyncThunk(
    'devices/editDevice',
    ({ id, name }) => api.put(`devices/${id}`, { device_name: name }),
  ),

  deleteDevice: createAsyncThunk(
    'devices/deleteDevice',
    token => api.post('user/logout', { device_token: token }).then(() => ({ token })),
  ),

  resetChannelsLockCode: createAsyncThunk(
    'devices/resetChannelsLockCode',
    async (id, { rejectWithValue }) => {
      try {
        return await api.post(`devices/${id}/pass-lock-restore`, { action: 'request'});
      } catch (error) {
        return rejectWithValue({
          message: error.data?.message || error.message,
          details: error.data?.details
        });
      }
    }
  ),

  // Device subscription operations
  reduceDeviceSlots: createAsyncThunk(
    'devices/reduceDeviceSlots',
    async (_, { rejectWithValue }) => {
      try {
        const response = await apiV2.delete('/device-subscriptions', {
          data: { extra_devices: 1 }
        });

        return {
          dailySavings: 0.25,
          newMaxDevices: response.new_max_devices,
          newDailyCost: response.new_daily_cost,
          subscriptions: response.subscriptions || [],
          extraDevices: (response.subscriptions || []).length
        };
      } catch (error) {
        return rejectWithValue({
          message: error.data?.message || error.message,
          details: error.data?.details
        });
      }
    }
  ),

  calculateDeviceSubscriptionCost: createAsyncThunk(
    'devices/calculateDeviceSubscriptionCost',
    async (extraDevices, { rejectWithValue }) => {
      try {
        return await apiV2.post('/device-subscriptions/calculate-cost', {
          extra_devices: extraDevices,
          days: 30
        });
      } catch (error) {
        return rejectWithValue({
          message: error.data?.message || error.message,
          details: error.data?.details
        });
      }
    }
  ),

  purchaseDeviceSubscription: createAsyncThunk(
    'devices/purchaseDeviceSubscription',
    async (extraDevices, { rejectWithValue }) => {
      try {
        const response = await apiV2.post('/device-subscriptions', {
          extra_devices: extraDevices
        });

        return {
          extraDevices,
          dailyCost: response.new_daily_cost || 0,
          newMaxDevices: response.new_max_devices,
          subscriptions: response.subscriptions || [],
          currentExtraDevices: (response.subscriptions || []).length
        };
      } catch (error) {
        return rejectWithValue({
          message: error.data?.message || error.message,
          details: error.data?.details
        });
      }
    }
  ),
};

const slice = createSlice({
  name: 'devices',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSubscriptionPurchaseResult: (state) => {
      state.subscriptionPurchaseResult = null;
    },
    setCurrentMaxDevices: (state, { payload }) => {
      state.currentMaxDevices = payload;
    },
    setCurrentExtraDevices: (state, { payload }) => {
      state.currentExtraDevices = payload;
    },
    initializeMaxDevicesFromUser: (state, { payload }) => {
      // Only set if currentMaxDevices is not already set from API responses
      if (state.currentMaxDevices === null && payload) {
        const defaultMaxDevices = 4; // fallback value
        state.currentMaxDevices = payload.device_subscriptions?.max_devices || defaultMaxDevices;
      }
    },
    initializeExtraDevicesFromUser: (state, { payload }) => {
      // Only set if currentExtraDevices is not already set from API responses
      if (state.currentExtraDevices === null && payload) {
        state.currentExtraDevices = payload.device_subscriptions?.extra_devices_count || 0;
      }
    },
  },
  extraReducers: builder =>
    builder
      // Device CRUD operations
      .addCase(actions.editDevice.fulfilled, (state, { payload }) => {
        state.devices[payload.device_token] = payload;
      })
      .addCase(actions.deleteDevice.fulfilled, (state, { payload }) => {
        state.devices = omit(state.devices, payload.token);
      })
      // Device subscription operations
      .addCase(actions.purchaseDeviceSubscription.fulfilled, (state, { payload }) => {
        state.subscriptionPurchaseResult = payload;

        // Update current max devices from API response
        if (payload.newMaxDevices) {
          state.currentMaxDevices = payload.newMaxDevices;
        }
        // Update current extra devices from API response
        if (payload.currentExtraDevices !== undefined) {
          state.currentExtraDevices = payload.currentExtraDevices;
        }
      })
      .addCase(actions.reduceDeviceSlots.fulfilled, (state, { payload }) => {
        // Update current max devices from API response
        if (payload.newMaxDevices) {
          state.currentMaxDevices = payload.newMaxDevices;
        }
        // Update current extra devices from API response
        if (payload.extraDevices !== undefined) {
          state.currentExtraDevices = payload.extraDevices;
        }
      })
      // Listen for fetchUser completion to update devices and user-related state
      .addCase('account/fetchUser/fulfilled', (state, { payload }) => {
        // Update devices list
        if (payload.devices) {
          state.devices = keyBy(payload.devices, 'device_token');
          localStorage.setItem('devices', JSON.stringify(state.devices));
        }

        // Update max devices and extra devices from fresh user data
        if (payload) {
          const defaultMaxDevices = 4;
          state.currentMaxDevices = payload.device_subscriptions?.max_devices || defaultMaxDevices;
          state.currentExtraDevices = payload.device_subscriptions?.extra_devices_count || 0;
        }
      })
});

export const devices = {
  reducer: slice.reducer,
  actions: {
    ...slice.actions,
    ...actions,
  },
  selectors: {
    devices: createSelector(
      state => state.devices.devices,
      devices => toArray(devices),
    ),
    devicesObject: createSelector(
      state => state.devices.devices,
      devices => devices,
    ),
    isLoading: createSelector(
      state => state.devices.isLoading,
      isLoading => isLoading,
    ),
    error: createSelector(
      state => state.devices.error,
      error => error,
    ),
    subscriptionPurchaseResult: createSelector(
      state => state.devices.subscriptionPurchaseResult,
      result => result,
    ),
    currentMaxDevices: createSelector(
      state => state.devices.currentMaxDevices,
      maxDevices => maxDevices,
    ),
    currentExtraDevices: createSelector(
      state => state.devices.currentExtraDevices,
      extraDevices => extraDevices,
    ),
    // Device limit selectors that access user state directly
    maxDevices: createSelector(
      [
        state => state.devices.currentMaxDevices,
        state => state.user?.user,
        state => state.user?.settings
      ],
      (currentMaxDevices, user, settings) => {
        // Use currentMaxDevices from API if available, otherwise fallback to user state
        if (currentMaxDevices !== null) {
          return currentMaxDevices;
        }
        const defaultMaxDevices = get(settings, 'user.max_devices', 4);
        return user?.device_subscriptions?.max_devices || defaultMaxDevices;
      }
    ),
    extraDevices: createSelector(
      [
        state => state.devices.currentExtraDevices,
        state => state.user?.user
      ],
      (currentExtraDevices, user) => {
        // Use currentExtraDevices from API if available, otherwise fallback to user state
        if (currentExtraDevices !== null) {
          return currentExtraDevices;
        }
        return user?.device_subscriptions?.extra_devices_count || 0;
      }
    ),
    hasReachedLimit: createSelector(
      [
        state => state.devices.devices,
        state => state.devices.currentMaxDevices,
        state => state.user?.user,
        state => state.user?.settings
      ],
      (devices, currentMaxDevices, user, settings) => {
        const count = toArray(devices).length;

        // Use currentMaxDevices from API if available, otherwise fallback to user state
        if (currentMaxDevices !== null) {
          return count >= currentMaxDevices;
        }

        const defaultMaxDevices = get(settings, 'user.max_devices', 4);
        const maxDevices = user?.device_subscriptions?.max_devices || defaultMaxDevices;
        return count >= maxDevices;
      }
    ),
    hasExtendedDevices: createSelector(
      [
        state => state.devices.currentExtraDevices,
        state => state.user?.user
      ],
      (currentExtraDevices, user) => {
        // Use currentExtraDevices from API if available, otherwise fallback to user state
        if (currentExtraDevices !== null) {
          return currentExtraDevices > 0;
        }
        return (user?.device_subscriptions?.extra_devices_count || 0) > 0;
      }
    ),
    canDecreaseDevices: createSelector(
      [
        state => state.devices.currentExtraDevices,
        state => state.user?.user,
        state => state.user?.settings
      ],
      (currentExtraDevices, user, settings) => {
        const couldAddDevices = !!get(settings, 'features.couldAddDevices', false);

        // Use currentExtraDevices from API if available, otherwise fallback to user state
        let hasExtendedDevices;
        if (currentExtraDevices !== null) {
          hasExtendedDevices = currentExtraDevices > 0;
        } else {
          hasExtendedDevices = (user?.device_subscriptions?.extra_devices_count || 0) > 0;
        }

        return hasExtendedDevices && couldAddDevices;
      }
    ),
  }
};
